/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Search, Upload, FileText, Loader2, CheckCircle2, AlertTriangle, Trash2, RefreshCw, Fingerprint, Send, Lock, BellRing, BadgeCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { searchPortalPatients, sendEncryptedReport, listSentReports, revokeReport, approveReport, getPatientPublicKey } from '@/lib/portal';

/**
 * SendReportPage — /referti (professionista)
 * Il PDF viene cifrato NEL BROWSER con la chiave pubblica del paziente prima dell'upload.
 * Workflow: consegna → APPROVAZIONE → notifica in background (email GDPR-safe senza dati
 * clinici + Magic Link 15' + notifica in-app). Ogni passo è tracciato nell'Audit Log SHA-256.
 * Deep-link: /referti?patient=<id> preseleziona il paziente (dai dettagli paziente).
 */

const STATUS_BADGE = {
  revoked: { label: 'revocato', variant: 'outline' },
  opened: { label: 'letto', variant: 'success' },
  approved: { label: 'approvato · notificato', variant: 'success' },
  delivered: { label: 'in attesa di approvazione', variant: 'outline' },
};

const CATEGORIES = [['referto', 'Referto'], ['esame', 'Esame di laboratorio'], ['ricetta', 'Ricetta'], ['lettera', 'Lettera di dimissione'], ['altro', 'Altro documento']];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const MAX_SIZE = 8 * 1024 * 1024;

export default function SendReportPage() {
  const [params] = useSearchParams();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [patient, setPatient] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('referto');
  const [note, setNote] = useState('');
  const [approve, setApprove] = useState(true);
  const [approving, setApproving] = useState(null);
  const [phase, setPhase] = useState(null); // 'encrypting' | 'uploading'
  const [sent, setSent] = useState([]);
  const [sentLoading, setSentLoading] = useState(true);
  const debounce = useRef(null);

  const loadSent = useCallback(async () => { setSentLoading(true); try { setSent(await listSentReports()); } catch { toast.error('Impossibile caricare gli invii'); } finally { setSentLoading(false); } }, []);
  useEffect(() => { loadSent(); }, [loadSent]);

  // Preselezione dal deep-link /referti?patient=<id> (azione "Carica / valida referto" nei dettagli paziente).
  useEffect(() => {
    const pid = params.get('patient');
    if (!pid) return;
    (async () => {
      try {
        const pub = await getPatientPublicKey(pid);
        setPatient({ id: pid, full_name: pub.patient_name || 'Paziente', email: '', has_public_key: true, fingerprint: pub.fingerprint });
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Il paziente non ha ancora attivato il Portale cifrato');
      }
    })();
  }, [params]);

  useEffect(() => {
    clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults([]); return; }
    debounce.current = setTimeout(async () => { setSearching(true); try { setResults(await searchPortalPatients(q.trim())); } catch { setResults([]); } finally { setSearching(false); } }, 300);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > MAX_SIZE) { toast.error('File troppo grande (max 8 MB)'); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[a-z0-9]+$/i, ''));
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!patient) { toast.error('Seleziona un paziente'); return; }
    if (!patient.has_public_key) { toast.error('Il paziente non ha ancora attivato il Portale cifrato'); return; }
    if (!file) { toast.error('Seleziona un documento'); return; }
    if (!title.trim()) { toast.error('Inserisci un titolo'); return; }
    try {
      setPhase('encrypting');
      await new Promise((r) => setTimeout(r, 50));
      const meta = await sendEncryptedReport({ patient, file, title: title.trim(), category, note: note.trim(), approve });
      setPhase(null);
      toast.success(approve ? `Referto approvato: notifica sicura in invio a ${patient.full_name}` : `Referto cifrato e consegnato a ${patient.full_name}`);
      setSent((l) => [{ ...meta, patient_name: patient.full_name, patient_email: patient.email }, ...l]);
      setFile(null); setTitle(''); setNote('');
    } catch (err) {
      setPhase(null);
      toast.error(err?.response?.data?.detail || err.message || 'Invio non riuscito');
    }
  };

  const approveOne = async (r) => {
    setApproving(r.id);
    try {
      const upd = await approveReport(r.id);
      setSent((l) => l.map((x) => (x.id === r.id ? { ...x, ...upd } : x)));
      toast.success(upd.re_notify ? 'Nuova notifica sicura inviata al paziente' : 'Referto approvato: il paziente riceverà email + link sicuro (15 min)');
      setTimeout(loadSent, 2500);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Approvazione non riuscita'); } finally { setApproving(null); }
  };

  const [revoking, setRevoking] = useState(null);
  const revoke = async (id) => {
    if (revoking) return;
    setRevoking(id);
    try { await revokeReport(id); setSent((l) => l.map((x) => (x.id === id ? { ...x, status: 'revoked' } : x))); toast.success('Referto revocato'); } catch (err) { toast.error(err?.response?.data?.detail || 'Revoca non riuscita'); } finally { setRevoking(null); }
  };

  return (
    <div className="space-y-5" data-testid="send-report-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><Lock className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display font-bold text-xl">Referti cifrati</h1>
            <p className="text-xs text-muted-foreground">Il documento viene cifrato in questo browser con la chiave pubblica del paziente: il server riceve solo dati illeggibili.</p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1 text-[10px] border-primary/40 text-primary"><ShieldCheck className="h-3 w-3" /> AES-GCM 256 + RSA-OAEP 2048</Badge>
      </div>

      <div className="grid gap-5 2xl:grid-cols-[1fr_1.1fr]">
        <Card className="rounded-xl" data-testid="send-report-form-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Nuovo invio</CardTitle>
            <CardDescription>1. Paziente · 2. Documento · 3. Cifra e consegna</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={send} className="space-y-4">
              {/* Paziente */}
              <div>
                <Label>Paziente</Label>
                {patient ? (
                  <div className={cn('rounded-xl border p-3 flex items-center gap-3', patient.has_public_key ? 'border-success/40 bg-success/5' : 'border-warning/50 bg-warning/10')} data-testid="send-report-selected-patient">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{patient.full_name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{patient.email}</div>
                    </div>
                    {patient.has_public_key
                      ? <Badge variant="success" className="text-[10px] gap-1"><Fingerprint className="h-3 w-3" /> {patient.fingerprint?.slice(0, 10)}</Badge>
                      : <Badge variant="outline" className="text-[10px] gap-1 border-warning text-warning"><AlertTriangle className="h-3 w-3" /> portale non attivato</Badge>}
                    <Button type="button" variant="ghost" size="sm" onClick={() => setPatient(null)} className="rounded-xl" data-testid="send-report-change-patient">Cambia</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o email (min 2 caratteri)" className="h-11 rounded-xl pl-9" data-testid="send-report-patient-search" />
                      {searching && <Loader2 className="h-4 w-4 absolute right-3 top-3.5 animate-spin text-muted-foreground" />}
                    </div>
                    {results.length > 0 && (
                      <ul className="rounded-xl border border-border/70 divide-y divide-border/70 max-h-56 overflow-auto" data-testid="send-report-patient-results">
                        {results.map((p) => (
                          <li key={p.id}>
                            <button type="button" onClick={() => { setPatient(p); setQ(''); setResults([]); }} className="w-full text-left px-3 py-2.5 hover:bg-muted/60 flex items-center gap-2 transition-colors" data-testid={`send-report-patient-${p.id}`}>
                              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{p.full_name}</div><div className="text-[11px] text-muted-foreground truncate">{p.email}</div></div>
                              {p.has_public_key ? <Badge variant="success" className="text-[10px]">chiave OK</Badge> : <Badge variant="outline" className="text-[10px]">senza chiave</Badge>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {q.trim().length >= 2 && !searching && results.length === 0 && <p className="text-xs text-muted-foreground" data-testid="send-report-patient-empty">Nessun paziente trovato.</p>}
                  </div>
                )}
              </div>

              {/* Documento */}
              <div>
                <Label>Documento (PDF o immagine, max 8 MB)</Label>
                <label className={cn('block rounded-xl border border-dashed p-4 cursor-pointer transition-colors hover:border-primary/60', file ? 'border-primary/50 bg-primary/5' : 'border-border')} data-testid="send-report-dropzone">
                  <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="send-report-file" />
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{file ? <FileText className="h-5 w-5" /> : <Upload className="h-5 w-5" />}</div>
                    <div className="text-sm">{file ? <><b>{file.name}</b> <span className="text-muted-foreground">· {fmtSize(file.size)}</span></> : <span className="text-muted-foreground">Clicca per selezionare il file</span>}</div>
                  </div>
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
                <div><Label>Titolo</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Referto ecografia addome" className="h-11 rounded-xl" data-testid="send-report-title" /></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="send-report-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Nota per il paziente (facoltativa, non cifrata)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} rows={2} className="rounded-xl" data-testid="send-report-note" /></div>

              <div className="rounded-xl border border-border/70 p-3 flex items-center gap-3" data-testid="send-report-approve-box">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><BellRing className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Approva e notifica subito il paziente</div>
                  <p className="text-[11px] text-muted-foreground">Email senza dati clinici + link di accesso sicuro (15 min, monouso) + notifica in-app. Tracciato nell'audit SHA-256.</p>
                </div>
                <Switch checked={approve} onCheckedChange={setApprove} aria-label="Approva e notifica" data-testid="send-report-approve-switch" />
              </div>

              <Button type="submit" disabled={!!phase || !patient?.has_public_key || !file} className="h-11 rounded-xl w-full" data-testid="send-report-submit">
                {phase ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {phase === 'encrypting' ? 'Cifratura nel browser…' : approve ? 'Cifra, approva e notifica' : 'Cifra e consegna al paziente'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="rounded-xl" data-testid="send-report-sent-card">
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <div><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Consegnati</CardTitle><CardDescription>Approvazione, notifica, stato di lettura e revoca.</CardDescription></div>
            <Button variant="ghost" size="sm" onClick={loadSent} disabled={sentLoading} className="rounded-xl" data-testid="send-report-refresh"><RefreshCw className={cn('h-4 w-4', sentLoading && 'animate-spin')} /></Button>
          </CardHeader>
          <CardContent>
            {sentLoading && sent.length === 0 ? <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
              : sent.length === 0 ? <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="send-report-sent-empty">Nessun referto inviato finora.</div>
              : (
                <ul className="space-y-2" data-testid="send-report-sent-list">
                  {sent.map((r) => (
                    <li key={r.id} className={cn('rounded-xl border border-border/70 p-3 flex items-center gap-3', r.status === 'revoked' && 'opacity-60')} data-testid={`send-report-sent-${r.id}`}>
                      <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-sm truncate">{r.title}</span>
                          {(() => { const b = STATUS_BADGE[r.status] || STATUS_BADGE.delivered; return <Badge variant={b.variant} className="text-[10px]" data-testid={`send-report-status-${r.id}`}>{b.label}</Badge>; })()}
                          {r.approved_at && r.status !== 'approved' && r.status !== 'revoked' && <Badge variant="outline" className="text-[10px] gap-1 border-success/50 text-success"><BadgeCheck className="h-3 w-3" /> approvato</Badge>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{r.patient_name || r.patient_id} · {fmtDate(r.created_at)} · {fmtSize(r.size)}{r.opened_at && ` · letto ${fmtDate(r.opened_at)}`}{r.notified_at && ` · notificato ${fmtDate(r.notified_at)}`}</div>
                      </div>
                      {r.status !== 'revoked' && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant={r.approved_at ? 'ghost' : 'outline'} size="sm" onClick={() => approveOne(r)} disabled={approving === r.id} className="rounded-xl h-9" aria-label={r.approved_at ? 'Invia nuova notifica' : 'Approva e notifica'} data-testid={`send-report-approve-${r.id}`}>
                                {approving === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : r.approved_at ? <BellRing className="h-4 w-4" /> : <><BadgeCheck className="h-4 w-4" /> Approva</>}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{r.approved_at ? 'Invia di nuovo la notifica sicura (nuovo link 15 min)' : 'Approva il referto e notifica il paziente'}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {r.status !== 'revoked' && <Button variant="ghost" size="sm" onClick={() => revoke(r.id)} disabled={revoking === r.id || approving === r.id} className="rounded-xl text-destructive hover:text-destructive" aria-label="Revoca" data-testid={`send-report-revoke-${r.id}`}>{revoking === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}</Button>}
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
